import { create } from 'zustand';
import * as UserServices from '../services/user.service';
import type { IUser } from '../interfaces/IUser';
import { getStorageValue, REFRESH_TOKEN_KEY } from '../services/storage.service';
import { identifyAnalyticsUser, trackAnalytics } from '@dotstorming/analytics';

interface AuthState {
  isLoading: boolean;
  error: string | null;
  user: IUser | null;
}

interface AuthActions {
  getMe: () => Promise<IUser | null>;
  setMe: (user: IUser | null) => Promise<void>;
  getRefreshToken: () => Promise<string | null>;
  refresh: (token: string) => Promise<void>;
  signup: (name: string, email: string, password: string) => Promise<boolean>;
  login: (email: string, password: string) => Promise<boolean>;
  loginWithGoogle: (credential: string) => Promise<boolean>;
  loginWithMicrosoft: (code: string, redirectUri: string) => Promise<boolean>;
  logout: () => void;
}

const getRefreshTokenFromStorage = async () => {
  return getStorageValue<string>(REFRESH_TOKEN_KEY);
};

export const useAuthStore = create<AuthState & AuthActions>((set, get) => ({
  isLoading: false,
  error: null,
  user: null,

  getMe: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await UserServices.getMe();

      set({ isLoading: false, user: response });
      return response;
    } catch (err: any) {
      set({ isLoading: false, error: err.message || 'Failed to fetch user' });
      return null;
    }
  },

  setMe: async (user: IUser | null) => {
    set({ user });
  },

  getRefreshToken: async () => {
    try {
      return await getRefreshTokenFromStorage();
    } catch (err: any) {
      set({ error: err.message || 'Failed to fetch refresh token' });
      return null;
    }
  },

  refresh: async (token: string) => {
    set({ isLoading: true, error: null });
    try {
      await UserServices.refreshToken(token);
      set({ isLoading: false });
    } catch (err: any) {
      set({
        isLoading: false,
        error: err.message || 'Failed to refresh token',
      });
    }
  },

  signup: async (name, email, password) => {
    set({ isLoading: true, error: null });
    try {
      await UserServices.createUser(name, email, password);
      const success = await get().login(email, password);
      const user = get().user;
      if (success && user) {
        identifyAnalyticsUser(user.id, { account_level: user.accountLevel });
        trackAnalytics('sign_up', { method: 'email', signup_source: 'signup_form' });
      }
      return success;
    } catch (err: any) {
      set({ isLoading: false, error: err.message || 'Signup failed' });
      return false;
    }
  },

  login: async (email, password) => {
    set({ isLoading: true, error: null });
    try {
      const response = await UserServices.authenticate(email, password);
      await UserServices.storeTokens(response.token, response.user);
      set({ isLoading: false, user: response.user });
      return true;
    } catch (err: any) {
      set({ isLoading: false, error: err.message || 'Login failed' });
      return false;
    }
  },

  loginWithGoogle: async (credential) => {
    set({ isLoading: true, error: null });
    try {
      const response = await UserServices.authenticateGoogle(credential);
      await UserServices.storeTokens(response.token, response.user);
      set({ isLoading: false, user: response.user });
      if (response.user.isNew) {
        identifyAnalyticsUser(response.user.id, { account_level: response.user.accountLevel });
        trackAnalytics('sign_up', { method: 'google', signup_source: 'login_form' });
      }
      return true;
    } catch (err: any) {
      set({ isLoading: false, error: err.message || 'Google login failed' });
      return false;
    }
  },

  loginWithMicrosoft: async (code, redirectUri) => {
    set({ isLoading: true, error: null });
    try {
      const response = await UserServices.authenticateMicrosoft(code, redirectUri);
      await UserServices.storeTokens(response.token, response.user);
      set({ isLoading: false, user: response.user });
      if (response.user.isNew) {
        identifyAnalyticsUser(response.user.id, { account_level: response.user.accountLevel });
        trackAnalytics('sign_up', { method: 'microsoft', signup_source: 'microsoft_callback' });
      }
      return true;
    } catch (err: any) {
      set({ isLoading: false, error: err.message || 'Microsoft login failed' });
      return false;
    }
  },

  logout: () => {
    void UserServices.clearTokens();
    set({ user: null });
  },
}));
